import { getStoryFeed } from '@api/story/story-feed';
import type { StoryFeedItem } from '@api/story/types';
import type { Experiment } from '@api/types';
import { SchemaTypes } from '@components/common/json-ld';
import { STORIES_COUNT } from '@components/public/main/gender-block-stories/constants';
import { APP_NAME } from '@shared/constants';
import { Competitor } from '@shared/route-types';
import { buildFullPath, getHrefLocale, URLS } from '@shared/routing';
import { Resolution } from '@shared/types';
import { error } from '@sveltejs/kit';

import type { PageLoad } from './$types';

import { browser } from '$app/environment';

export const ssr = true;

export const load = (async ({ url, params }) => {
    const competitor = params.competitor as Competitor;
    const lang = getHrefLocale(url);
    const allCompetitors = Object.values(Competitor);
    if (!allCompetitors.includes(competitor)) {
        throw error(404, { code: 404, message: 'Not found' });
    }

    if (!browser) {
        return { stories: [], competitor, lang };
    }

    let experiment: Experiment | null = null;
    let stories: StoryFeedItem[] = [];
    try {
        const competitorIdx = allCompetitors.findIndex(c => c === competitor);
        const data = await getStoryFeed({
            page: competitorIdx + 2, // skip first page (these are stories for the main page)
            itemsPerPage: STORIES_COUNT,
            group: 'seo',
            resolution: Resolution.p480,
        });
        stories = data.items;
        experiment = data.experiment;
    } catch (e) {
        console.error(e);
    }

    const currentUrl = new URL(url.href);
    const mainUrlWithLang = buildFullPath('/', currentUrl.origin, {}, lang);

    const jsonLd = {
        '@type': SchemaTypes.Article as SchemaTypes.Article,
        '@id': `${currentUrl.href}#article`,
        url: currentUrl.href,
        hasPart: stories?.map((story) => ({
            '@type': SchemaTypes.VideoObject as SchemaTypes.VideoObject,
            '@id': `${URLS.storyOpen(story.id, currentUrl.href)}`,
            contentUrl: `${story.videoUrl}`,
            name: `Short ${story.operatorDisplayName} Video: TikTok & Vertical Reels`,
            thumbnailUrl: `${story.previewImgUrl}`,
            description: `Watch ${story.operatorDisplayName} video on Flirtify`,
            uploadDate: story.updatedAt,
        })) ?? undefined,
        inLanguage: lang,
        datePublished: '2025-02-11T15:20:06+03:00',
        dateModified: '2025-07-01T15:20:06+03:00',
        potentialAction: {
            '@type': SchemaTypes.ReadAction as SchemaTypes.ReadAction,
            target: currentUrl.href,
        },
        isPartOf: {
            '@type': SchemaTypes.WebSite as SchemaTypes.WebSite,
            '@id': `${mainUrlWithLang}#website`,
            name: APP_NAME,
            url: mainUrlWithLang,
        },
        author: {
            '@type': SchemaTypes.Organization as SchemaTypes.Organization,
            url: mainUrlWithLang,
            name: APP_NAME,
        },
    };

    return {
        stories, competitor, experiment, jsonLd, lang,
    };
}) satisfies PageLoad;
